import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { RiBellLine, RiMailLine, RiSmartphoneLine } from 'react-icons/ri';
import { useGetParametresFiscauxQuery } from 'actions/fiscalite/queries';
import { useUpdateParametresFiscauxMutation } from 'actions/fiscalite/mutations';
import { toastApiErrors } from 'utils/toastApiError';

/**
 * Paramètres d'alerte des échéances fiscales (par dossier).
 * Les envois effectifs (email/SMS) seront branchés sur l'infrastructure de
 * notification dans une itération ultérieure ; les préférences sont déjà
 * persistées en base.
 */
const ParametresFiscaux = ({ dossierId, canEdit }) => {
  const { data, isLoading } = useGetParametresFiscauxQuery(
    { dossier_id: dossierId },
    { skip: !dossierId }
  );
  const [updateParametres, { isLoading: isSaving }] =
    useUpdateParametresFiscauxMutation();

  const [form, setForm] = useState({
    alerte_email: true,
    alerte_sms: false,
    jours_avant_alerte: 5,
  });

  useEffect(() => {
    if (data) {
      setForm({
        alerte_email: data.alerte_email ?? true,
        alerte_sms: data.alerte_sms ?? false,
        jours_avant_alerte: data.jours_avant_alerte ?? 5,
      });
    }
  }, [data]);

  const handleSave = async () => {
    try {
      await updateParametres({ dossier_id: dossierId, ...form }).unwrap();
      toast.success('Paramètres de rappel enregistrés.');
    } catch (error) {
      toastApiErrors(error);
    }
  };

  if (isLoading) return null;

  return (
    <div className="fisc-panel" style={{ maxWidth: 720 }}>
      <div className="fisc-panel-header">
        <RiBellLine size={20} className="fisc-panel-icon" />
        <div>
          <h3>Rappels &amp; notifications d&rsquo;échéances fiscales</h3>
          <p>Alertes automatiques avant le 15 du mois pour éviter toute pénalité DGID</p>
        </div>
      </div>

      <div className="fisc-setting-row">
        <div className="d-flex align-items-center" style={{ gap: 10 }}>
          <RiSmartphoneLine size={18} className="fisc-panel-icon" />
          <div>
            <div className="fw-bold" style={{ fontSize: 13 }}>Alertes SMS &amp; WhatsApp</div>
            <div className="text-muted" style={{ fontSize: 12 }}>
              SMS de rappel avant l&rsquo;échéance du 15 du mois (actif dès qu&rsquo;un provider SMS est configuré)
            </div>
          </div>
        </div>
        <input
          type="checkbox"
          checked={form.alerte_sms}
          disabled={!canEdit}
          onChange={(e) => setForm({ ...form, alerte_sms: e.target.checked })}
          style={{ width: 18, height: 18 }}
        />
      </div>

      <div className="fisc-setting-row">
        <div className="d-flex align-items-center" style={{ gap: 10 }}>
          <RiMailLine size={18} className="fisc-panel-icon" />
          <div>
            <div className="fw-bold" style={{ fontSize: 13 }}>Alertes e-mail quotidiennes</div>
            <div className="text-muted" style={{ fontSize: 12 }}>
              Récapitulatif des échéances proches envoyé chaque jour au comptable du dossier
            </div>
          </div>
        </div>
        <input
          type="checkbox"
          checked={form.alerte_email}
          disabled={!canEdit}
          onChange={(e) => setForm({ ...form, alerte_email: e.target.checked })}
          style={{ width: 18, height: 18 }}
        />
      </div>

      <div className="fisc-setting-row flex-column align-items-start">
        <label className="fw-bold" style={{ fontSize: 13 }}>
          Déclencher l&rsquo;alerte préventive combien de jours avant la date butoir ?
        </label>
        <select
          className="form-control"
          value={form.jours_avant_alerte}
          disabled={!canEdit}
          onChange={(e) =>
            setForm({ ...form, jours_avant_alerte: Number(e.target.value) })
          }
        >
          <option value={10}>10 jours avant (le 5 du mois)</option>
          <option value={5}>5 jours avant (le 10 du mois) - Recommandé</option>
          <option value={2}>2 jours avant (le 13 du mois)</option>
          <option value={1}>1 jour avant (le 14 du mois)</option>
        </select>
      </div>

      {canEdit && (
        <button
          className="fisc-btn-primary mt-3"
          onClick={handleSave}
          disabled={isSaving}
        >
          <RiBellLine style={{ marginRight: 6 }} />
          {isSaving ? 'Enregistrement...' : 'Enregistrer la configuration'}
        </button>
      )}
    </div>
  );
};

export default ParametresFiscaux;
